import { computed, inject, Injectable } from '@angular/core';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    EditModeStateService,
    EditState,
    EntityEdit,
    LayerSnapshot,
} from '@connect-services/client/services/edit-mode-state.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { DepositWithFolderAndStandard, EditAction, LayerWithAttributes } from '@connect-services/src/sdk/api';

export interface CreatedEntitiesByLayer {
    entities: CreatedEntityTableRow[];
    layerId: string;
    layerName: string;
}

export type CreatedEntityTableRow = Record<string, unknown> & {
    businessIdentifier: unknown;
    businessIdentifierKey?: string;
    deposit_id: string | null;
    uuid: string;
};

@Injectable({
    providedIn: 'root',
})
export class EntityCreatedTableService {
    public readonly createdEntitiesByLayer = computed<CreatedEntitiesByLayer[]>(() => {
        const project = this.projectBloc.value();

        if (!project) {
            return [];
        }

        this.editModeStateService.getStateTick();

        const layersWithAttributes: LayerWithAttributes[] = this.projectEntityTableService.layers();
        const state: EditState = this.editModeStateService.getState(project.id);

        return this.buildCreatedEntitiesByLayer(state, layersWithAttributes);
    });

    private readonly editModeStateService = inject(EditModeStateService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEntityTableService = inject(ProjectEntityTableService);

    private buildCreatedEntitiesByLayer(state: EditState, layers: LayerWithAttributes[]): CreatedEntitiesByLayer[] {
        return Object.entries(state)
            .map(([layerId, layerSnapshot]: [string, LayerSnapshot]) => {
                const entities: CreatedEntityTableRow[] = this.buildCreatedEntityRows(layerSnapshot);

                if (!entities.length) {
                    return null;
                }

                const layer: LayerWithAttributes | undefined = layers.find((currentLayer): boolean => {
                    return currentLayer.id === layerId;
                });

                return {
                    entities,
                    layerId: layer?.id,
                    layerName: layer?.name ?? layerId,
                };
            })
            .filter((createdEntitiesByLayer): createdEntitiesByLayer is CreatedEntitiesByLayer => {
                return !!createdEntitiesByLayer?.layerId;
            });
    }

    private buildCreatedEntityRows(layerSnapshot: LayerSnapshot): CreatedEntityTableRow[] {
        return Object.entries(layerSnapshot)
            .filter(([, entityEdit]: [string, EntityEdit]) => {
                return entityEdit.action === EditAction.CREATE;
            })
            .map(([entityId, entityEdit]: [string, EntityEdit]) => {
                const properties = entityEdit.properties ?? {};
                const dataName: string | null = this.getDepositFolderName(properties['deposit_id']);

                return {
                    ...properties,
                    businessIdentifier: entityEdit.businessIdentifier.value,
                    businessIdentifierKey: entityEdit.businessIdentifier.key,
                    deposit_id: dataName,
                    uuid: entityId,
                };
            });
    }

    private getDepositFolderName(depositId: string): string | null {
        if (!depositId) {
            return null;
        }

        const deposit: DepositWithFolderAndStandard | undefined = this.projectEntityTableService
            .deposits()
            .get(depositId);

        return deposit?.depositFolder?.name ?? null;
    }
}
